import { useContext, useMemo } from 'react';
import { AuthContext } from '../auth/Auth';
import { useLoginState } from '../studioxr/auth/LoginState';
import { firebaseHelper, resolveWorkspacePro, useCreditWorkspace, useDesignFirm, useIsActiveRosterMember, workspacesEnabled } from 'ui';
import { UserProfileRole } from 'ts-interfaces';
import { isTeamPlanActive } from 'ui/utils/team/teamRole';

/**
 * Pro access, resolved against the CAPABILITY workspace (see below).
 *
 * Two distinct notions live here (do not conflate them — see
 * docs/workspaces-architecture.md):
 *
 *   - `isPro` / `isPersonalPro` / `isTeamPro` — the WORKSPACE-scoped capability
 *     signal. `isPro` follows the capability workspace: a user can be Pro in a
 *     team workspace while Basic in their personal workspace (and vice-versa).
 *     This is the signal feature gates (downloads, uploads, file menu, projects
 *     tab, deep render, etc.) should read.
 *   - `isProUser` / `isTeamsProUser` / `isRegularProUser` — the membership-wide
 *     signal kept for the sharing UI (share-with-team vs share-with-individual),
 *     which must stay membership-wide so a team member acting in their Personal
 *     workspace still sees team-sharing options. Unchanged semantics.
 *
 * Personal Pro is `profile.userRole === PRO`; once the team-seat writers are
 * removed (see plan Phase 2) that means a personal entitlement only (personal
 * Stripe sub OR admin-assigned pro). Team Pro is derived: an active designer
 * seat in the active firm whose plan is live (`isTeamPlanActive`: a subscription or a contract team).
 */
export const useTeamsProAccess = () => {
  const { currentUser } = useContext(AuthContext);
  const profile = useLoginState((state) => state.profile);
  // Permissions come from LoginState — the source the create-team wizard
  // refreshes atomically (refreshPermissions). usePermissions keeps its own
  // module-level cache that the wizard does NOT refresh; feeding that stale
  // copy into useWorkspaceContext made its reconciliation bounce a
  // freshly-created 2nd team's workspace back to Personal.
  const permissions = useLoginState((state) => state.permissions);
  // Membership-wide team-Pro signal for the sharing UI: "active designer in any
  // team", computed in LoginState.updateTeamsProAccess (the single, prod-safe,
  // active-roster-aware source). It is INDEPENDENT of personal Pro — a team
  // designer with no personal sub still gets team-sharing.
  const storeIsTeamsProUser = useLoginState((state) => state.isTeamsProUser);
  // Capability workspace — the workspace whose Pro entitlement applies. Mirrors
  // useCreditWorkspace exactly (Pro capability and credits MUST resolve to the
  // same workspace). When workspaces are ENABLED this is just the active
  // workspace. When GATED in production (active forced to Personal, no
  // switcher) it pins to the user's first DESIGNER-seat team — without this, a
  // team designer with no personal sub loses EVERY Pro capability in prod once
  // the Phase-2 backfill clears their global userRole (active=Personal →
  // resolveWorkspacePro returns isPersonalPro=false). A pure admin / non-member
  // falls through to Personal.
  const workspace = useCreditWorkspace(currentUser?.uid ?? null, permissions);
  const firmId = workspace.kind === 'team' ? workspace.firmId : permissions?.teamsDesigner?.[0];
  const designFirm = useDesignFirm(firebaseHelper.db, firmId);
  // Active-roster membership for the CAPABILITY firm specifically — NOT the
  // active workspace. In gated production the active workspace is Personal and
  // useWorkspaceContext.isActiveMemberOfActiveWorkspace is hardcoded true, so
  // reading it here would bypass the roster gate and leak Pro to inactive
  // (deactivated) team designers whose seat still lingers in teamsDesigner.
  // Resolve the credit-workspace firm's live roster status directly instead.
  const isActiveRosterMember = useIsActiveRosterMember(
    currentUser?.uid ?? null,
    workspace.kind === 'team' ? workspace.firmId : null,
  );

  // Memoize the expensive calculations to prevent unnecessary re-calculations
  const accessStatus = useMemo(() => {
    // Personal entitlement (personal Stripe sub OR admin-assigned pro).
    const isPersonalPro = (profile as any)?.userRole === UserProfileRole.PRO;

    // Workspace-scoped capability Pro — follows the capability workspace.
    // The team branch requires an active designer seat in that firm AND a live
    // plan: a subscription (stripeSubscriptionId, deleted by the team-cancel
    // webhook) or a staff-set contract team.
    const teamSubActive = isTeamPlanActive(designFirm?.firm);
    const teamOrWorkspacePro = resolveWorkspacePro({
      workspace,
      isPersonalPro,
      permissions,
      isActiveRosterMember,
      teamSubActive,
    });
    // Gated-prod safety net: when workspaces are gated the capability workspace
    // is FORCE-pinned to the user's first designer-seat team (no switcher). A
    // personal-Pro user who is also a designer in a team whose subscription has
    // ended would then pin to that dead team, resolve to Basic, and silently
    // lose Pro capabilities they still pay for personally — with no way to
    // switch out. Personal entitlement always wins when gated. When workspaces
    // are ENABLED the user picks the workspace and can switch back to Personal,
    // so capabilities intentionally follow the active workspace (no override).
    const isPro = teamOrWorkspacePro || (!workspacesEnabled() && isPersonalPro);
    // isTeamPro tracks genuine TEAM entitlement (drives the "Pro Teams" badge),
    // so keep it on teamOrWorkspacePro — a personal-Pro user rescued by the
    // gated fallback above must NOT be mislabeled a team-Pro user.
    const isTeamPro = workspace.kind === 'team' && teamOrWorkspacePro;

    // Membership-wide signals for the sharing UI. isTeamsProUser is a genuine
    // "active designer in any team" signal (from LoginState), DECOUPLED from
    // personal Pro — so a team designer with userRole=basic still keeps the
    // team-sharing surface (share-with-team, "Shared with Me", full
    // collections). isRegularProUser is personal Pro that is NOT team Pro.
    const isTeamsProUser = storeIsTeamsProUser;
    const isProUser = isPersonalPro;
    const isRegularProUser = isPersonalPro && !isTeamsProUser;

    return {
      // workspace-scoped capability signals
      isPro,
      isPersonalPro,
      isTeamPro,
      // membership-wide signals (sharing UI)
      isProUser,
      isTeamsProUser,
      isRegularProUser,
      designFirm,
    };
  }, [
    (profile as any)?.userRole,
    workspace,
    permissions,
    isActiveRosterMember,
    storeIsTeamsProUser,
    (designFirm?.firm as any)?.stripeSubscriptionId,
    (designFirm?.firm as any)?.contractTeam,
    designFirm?.firm?.name,
    designFirm,
  ]);

  return accessStatus;
};
